import { useCallback, useEffect, useRef, useState } from "react";
import { FileWarning } from "lucide-react";
import type { QuoteDocumentTemplateDetail } from "@forgepoint/shared";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { mergeAssetUrls, type AssetUrlOverrides } from "./assetUrlOverrides";
import { QuoteDocumentSkeleton } from "./QuoteDocumentSkeleton";
import { QuoteTemplateEditor } from "./QuoteTemplateEditor";
import { useQuoteDocumentTemplatesStore } from "./quoteDocumentTemplatesStore";

/** Shown when the template's document cannot be read. */
const LOAD_ERROR = "The template could not be loaded.";

interface EditQuoteTemplateDialogProps {
  /** The template to edit, or null to keep the dialog closed. */
  templateId: string | null;
  /** Closes the dialog. */
  onClose: () => void;
}

/**
 * Full-screen host for the template editor. The settings panel is too narrow
 * for the three-pane editor, so editing a template takes over the screen the
 * same way editing a quote's document does. The template's document is fetched
 * on open, so the editor is seeded once from resolved HTML.
 * @param templateId - The template to edit, or null to keep the dialog closed
 * @param onClose - Closes the dialog
 * @returns The template-editor dialog element
 */
export function EditQuoteTemplateDialog({
  templateId,
  onClose,
}: EditQuoteTemplateDialogProps) {
  const fetchTemplate = useQuoteDocumentTemplatesStore(
    (state) => state.fetchTemplate,
  );
  const [template, setTemplate] = useState<QuoteDocumentTemplateDetail | null>(
    null,
  );
  const [loadFailed, setLoadFailed] = useState(false);
  const [isDirty, setIsDirty] = useState(false);
  const [confirmDiscardOpen, setConfirmDiscardOpen] = useState(false);
  const [assetOverrides, setAssetOverrides] = useState<AssetUrlOverrides>({});
  const overrideUrlsRef = useRef<string[]>([]);

  useEffect(() => {
    if (!templateId) {
      return;
    }
    let active = true;
    setTemplate(null);
    setLoadFailed(false);
    setIsDirty(false);
    fetchTemplate(templateId).then((detail) => {
      if (!active) {
        return;
      }
      if (!detail) {
        setLoadFailed(true);
        return;
      }
      setTemplate(detail);
    });
    return () => {
      active = false;
    };
  }, [fetchTemplate, templateId]);

  useEffect(
    () => () => {
      overrideUrlsRef.current.forEach((url) => URL.revokeObjectURL(url));
    },
    [],
  );

  const registerLocalAsset = useCallback((assetId: string, file: File) => {
    const objectUrl = URL.createObjectURL(file);
    overrideUrlsRef.current = [...overrideUrlsRef.current, objectUrl];
    setAssetOverrides((current) => ({ ...current, [assetId]: objectUrl }));
  }, []);

  const requestClose = () => {
    if (isDirty) {
      setConfirmDiscardOpen(true);
      return;
    }
    onClose();
  };

  const discardAndClose = () => {
    setConfirmDiscardOpen(false);
    setIsDirty(false);
    onClose();
  };

  return (
    <>
      <Dialog
        open={templateId !== null}
        onOpenChange={(open) => !open && requestClose()}
      >
        <DialogContent
          aria-describedby={undefined}
          onEscapeKeyDown={(event) => event.preventDefault()}
          className="inset-0 flex h-full w-full max-w-none translate-x-0 translate-y-0 flex-col gap-0 rounded-none border-0 p-0 sm:rounded-none [&>button]:hidden"
        >
          <DialogTitle className="sr-only">Edit template</DialogTitle>
          {template ? (
            <QuoteTemplateEditor
              key={template.id}
              templateId={template.id}
              templateName={template.name}
              seedDocument={template.document}
              assetUrls={mergeAssetUrls(template.assetUrls, assetOverrides)}
              onLocalAsset={registerLocalAsset}
              onDirtyChange={setIsDirty}
              onRequestClose={requestClose}
            />
          ) : (
            <div className="h-full overflow-auto bg-muted">
              {loadFailed ? (
                <div className="flex h-full flex-col items-center justify-center gap-3 px-6 text-center">
                  <FileWarning className="size-8 text-muted-foreground" />
                  <p className="text-sm font-medium">{LOAD_ERROR}</p>
                  <Button variant="outline" size="sm" onClick={onClose}>
                    Close
                  </Button>
                </div>
              ) : (
                <QuoteDocumentSkeleton />
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
      <AlertDialog
        open={confirmDiscardOpen}
        onOpenChange={setConfirmDiscardOpen}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Discard unsaved changes?</AlertDialogTitle>
            <AlertDialogDescription>
              The template has unsaved changes. Closing the editor will discard
              them.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Keep editing</AlertDialogCancel>
            <AlertDialogAction onClick={discardAndClose}>
              Discard changes
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
